import { DaysLeftBadge, ScheduleChart, useExpensesSchedule } from "@gladis/domain-expenses/ui";
import { formatEur, type MemberDto, type SchedulePeriod } from "@gladis/shared";
import { tRich, useT } from "@gladis/ui/i18n";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { filterSummary, type HealthFilterState } from "./health-filters.js";

/** How many upcoming payments the card names — enough to answer "what is next?", no more. */
const NEXT_SHOWN = 2;

/**
 * "Still to go out" — the Dashboard's view of the Expenses page section (feature 024, FR-029 / FR-030).
 *
 * THE SAME READ AS THE SECTION, under the same cache key: `GET /api/expenses/schedule`. While both show the same
 * period its figure EQUALS the section's headline by construction rather than by agreement (SC-003), and it adds
 * nothing to the Dashboard's own two reads — the page read stays at 22 and the health read at 13.
 *
 * IT FOLLOWS BOTH FILTERS (amended 2026-10-26). The period filter offers this month and this year, which ARE
 * the schedule's month and year, so there is nothing to translate. `Out` beside it reads "this year" as the
 * year so far while this card reads the whole calendar year — the muted part of the chart IS the year so far,
 * so the two describe one window from two sides, under two names. It still sits beside Recent activity and
 * not in the row of figures: `Out` is a monthly equivalent, this is cash due, and one card holding both would
 * show two figures for one month.
 *
 * NOT A FORECAST: every figure is a payment the household entered, due on a date the household entered.
 */
export function StillToGoOutCard({
  filters,
  members,
}: {
  filters: HealthFilterState;
  members: readonly MemberDto[];
}): ReactNode {
  const t = useT();
  const period: SchedulePeriod = filters.period === "this-year" ? "year" : "month";
  // The Dashboard's `household` is the expenses read's absent member; a member id is passed as it is.
  const { data } = useExpensesSchedule(
    period,
    filters.scope === "household" ? undefined : filters.scope,
  );

  const next = (data?.steps ?? [])
    .filter((step) => step.date >= (data?.asOf ?? ""))
    .flatMap((step) => step.payments.map((payment) => ({ date: step.date, payment })))
    .slice(0, NEXT_SHOWN);

  return (
    <section
      aria-labelledby="still-to-go-out-heading"
      data-testid="still-to-go-out-card"
      className="flex flex-col rounded-control border border-line bg-panel p-5"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2
          id="still-to-go-out-heading"
          className="text-xs font-medium tracking-wide text-muted uppercase"
        >
          {t("dash.stillToGoOut.heading")}
        </h2>
        <Link
          to="/expenses"
          hash="still-to-go-out"
          className="text-xs text-muted underline hover:text-ink"
        >
          {t("dash.stillToGoOut.view")}
        </Link>
      </div>

      {data === undefined ? null : (
        <>
          <p className="mt-3 text-sm text-muted" data-testid="still-to-go-out-figure">
            {tRich("dash.stillToGoOut.figure", {
              remaining: (
                <span className="text-2xl font-semibold text-ink tabular-nums">
                  {formatEur(data.totals.remainingInEuros)}
                </span>
              ),
              total: (
                <span className="tabular-nums">
                  {data.totals.isEstimate ? "≈ " : ""}
                  {formatEur(data.totals.periodTotalInEuros)}
                </span>
              ),
            })}
          </p>
          <p className="mt-1 text-xs text-muted">{filterSummary(filters, members)}</p>

          <div className="mt-3">
            <ScheduleChart data={data} period={period} compact />
          </div>

          {next.length === 0 ? (
            <p className="mt-3 text-sm text-muted">{t("dash.stillToGoOut.nothingLeft")}</p>
          ) : (
            <ol
              aria-label={t("dash.stillToGoOut.nextHeading")}
              className="mt-3 flex flex-col gap-1.5"
            >
              {next.map(({ date, payment }) => (
                <li
                  key={`${date}-${payment.rowKey}`}
                  className="flex items-center justify-between gap-3 text-sm"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <DaysLeftBadge date={date} asOf={data.asOf} />
                    <span className="min-w-0 truncate">{payment.description}</span>
                  </span>
                  <span className="shrink-0 tabular-nums">{formatEur(payment.amountInEuros)}</span>
                </li>
              ))}
            </ol>
          )}

          {data.undated.length === 0 ? null : (
            <p className="mt-auto pt-3 text-xs text-muted" data-testid="still-to-go-out-undated">
              {t("dash.stillToGoOut.undated", { count: data.undated.length })}{" "}
              <Link to="/expenses" hash="still-to-go-out" className="underline hover:text-ink">
                {t("dash.stillToGoOut.setDates")}
              </Link>
            </p>
          )}
        </>
      )}
    </section>
  );
}
